// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Tithe — the solo opponent. In a deep abstract the bot IS the product for one player, so this is a
// real engine: negamax with alpha-beta, iterative deepening, a transposition table, killer and
// history ordering, a capture quiescence and a positional evaluation — over the same rules.ts every
// human move goes through.
//
// Five things here are deliberate and load-bearing:
//
//   * NO requestAnimationFrame ANYWHERE. The search is budgeted with Date.now() and started from a
//     setTimeout. A backgrounded tab throttles rAF to nothing, and a bot that cannot finish its
//     turn is a hung game, not a slow one.
//   * THE SIM DRIVES THE SAME SEARCH, budgeted by DEPTH and a node cap rather than a wall clock, so
//     tests/balance.test.ts is reproducible instead of machine-dependent.
//   * The node pool copies into preallocated states rather than allocating one per node.
//   * NO NULL-MOVE PRUNING, and please do not add it. Null-move assumes that doing nothing is never
//     better than doing something. Here every turn HANDS THE OPPONENT A TOLL, so "pass" is a
//     materially different action from "do nothing" and the zugzwang assumption simply does not
//     hold — Ledger in particular is a game about being forced to give something up.
//   * Repetition is a real rule (three occurrences and the side that made it happen loses), so the
//     search carries the game's own repetition counts into its path and scores the third occurrence
//     as a LOSS for the mover, not as a draw. Without that the bot cheerfully shuffles into its own
//     defeat when it is behind.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import {
  COFFER,
  NC,
  PH_CHOOSE,
  PH_PLAY,
  PH_SPLIT,
  PPS,
  W,
  copyInto,
  cloneState,
  cx,
  cy,
  genPlies,
  isKeeper,
  isOver,
  keeperOf,
  orient,
  plyFrom,
  plyKind,
  plyTo,
  positionHash,
  K_MOVE,
  seatOfPiece,
  step,
  type State,
} from './rules';
import { isTollId, tollOf } from './tolls';

export interface Tier {
  id: string;
  name: string;
  blurb: string;
  depth: number;
  timeMs: number;
  /** Novice: the lookahead terms are zeroed, so it takes what is offered but sets no traps. */
  flat: boolean;
  noise: number;
}

export const TIERS: Tier[] = [
  { id: 'novice', name: 'Novice', blurb: 'Takes what you leave hanging and never blunders a win — but sets no traps.', depth: 2, timeMs: 90, flat: true, noise: 16 },
  { id: 'adept', name: 'Adept', blurb: 'Looks several turns ahead and will punish a toll you could not afford to pay.', depth: 5, timeMs: 400, flat: false, noise: 3 },
  // Depth 7 measured at 0.7–1.5s on this machine, so the 900ms cap is what actually binds and the
  // bot usually returns a depth-6 or depth-7 move. Verified again in the browser pass on a phone
  // viewport, because "fits in the budget" is a claim about the slowest device, not the fastest.
  { id: 'reckoner', name: 'Reckoner', blurb: 'Searches deep, counts what you can do with the toll you just handed over, and closes.', depth: 7, timeMs: 900, flat: false, noise: 0 },
];

export const DEFAULT_TIER = 'novice';

export function tierOf(id: string | null | undefined): Tier {
  if (typeof id === 'string') {
    const t = TIERS.find((x) => x.id === id);
    if (t) return t;
  }
  return TIERS[0];
}

const MATE = 1_000_000;

export interface SearchOpts {
  depth: number;
  /** Wall-clock cap in ms. 0 disables it — which is what the sim uses. */
  timeMs?: number;
  /** Hard node cap, so a sim run is bounded regardless of machine speed. */
  nodes?: number;
  flat?: boolean;
  /** Small random tie-break so repeat games are not byte-identical. */
  noise?: number;
  rng?: Rng;
  /** Override the evaluation weights — the sim's ablation arms pass BLIND_W. */
  weights?: EvalWeights;
  /**
   * The balance sim's negative-GAP control: search as if the toll exchange never happened, so this
   * bot cannot see that the toll it pays arms its opponent. It still plays a legal game — the real
   * `step` outside the search rotates the tolls as always — it just plans as though it did not.
   */
  frozenTolls?: boolean;
}

export interface SearchResult {
  ply: number;
  score: number;
  depth: number;
  nodes: number;
  ms: number;
}

// ── evaluation ──────────────────────────────────────────────────────────────────
/**
 * Every weight in one object, because the balance sim has to be able to zero individual terms and
 * MEASURE what each is worth. That is not a nicety: the first version of this eval scored holding a
 * high-value toll as a good thing, and a control arm blind to exactly that term beat the full
 * evaluation 9 games in 10. A weight you cannot switch off is a weight you are taking on faith.
 */
export interface EvalWeights {
  bearer: number;
  keeperHit: number;
  /** Their Keeper-shot needs a toll they do not hold YET but could pick up off the table. */
  keeperHitTable: number;
  double: number;
  cofferHit: number;
  advance: number;
  home: number;
  activity: number;
  cover: number;
  /**
   * What the tolls in your hand are worth to hold — option value, mildly positive. The first
   * version made this NEGATIVE on the theory that "a toll you hold is a toll you are about to hand
   * over"; the sim's seat-controlled arms then showed a control blind to it winning 96% as seat 0
   * AND 96% as seat 1, which is what a term with the wrong sign looks like. Reasoning your way to a
   * weight is exactly the thing principle #18 exists to stop.
   */
  hand: number;
  /**
   * The tolls on the table are picked up by whoever moves next. Deliberately SMALL: the first
   * version credited them to the side to move, which is a constant penalty for being to move — a
   * systematic anti-tempo bias inside a negamax, and a much bigger deal than its size suggests.
   */
  table: number;
}

export const DEFAULT_W: EvalWeights = {
  bearer: 100,
  keeperHit: 320,
  keeperHitTable: 70,
  double: 90,
  cofferHit: 330,
  advance: 18,
  home: 24,
  activity: 2,
  cover: 4,
  // MEASURED AT ZERO. Ablating both of these across three modes moved the win rate by nothing the
  // sample could see (the sighted arm won 40–48% against a control with them switched off, at
  // n=30 per mode). The honest reading is that the SEARCH already knows what the handover does —
  // it plays through the exchange at every node — so an evaluation term for it is a second, worse
  // copy of information the engine already has. They are kept as switchable weights rather than
  // deleted so the ablation stays reproducible.
  hand: 0,
  table: 0,
};

/** Handover-blind weights, kept for the sim's per-term ablation arms. */
export const BLIND_W: EvalWeights = { ...DEFAULT_W, hand: 0, table: 0, keeperHitTable: 0 };

/** Centre-weighted, because on a 5×5 the middle ring reaches everything. */
const PSQT = [0, 1, 2, 1, 0, 1, 3, 4, 3, 1, 2, 4, 6, 4, 2, 1, 3, 4, 3, 1, 0, 1, 2, 1, 0];

/** Reused scratch — the evaluation runs at every leaf and must not allocate. */
const ATK: [Int8Array, Int8Array] = [new Int8Array(NC), new Int8Array(NC)];
const ATK_TABLE: [Int8Array, Int8Array] = [new Int8Array(NC), new Int8Array(NC)];

/**
 * How many of `seat`'s pieces could land on each cell next turn. `extra` adds the tolls sitting on
 * the table, which is the softer threat: they do not hold that toll yet, but they can pick it up.
 */
function fillAttacks(s: State, seat: number, out: Int8Array, extra: boolean): void {
  out.fill(0);
  const tolls = [s.hand[seat * 2], s.hand[seat * 2 + 1]];
  if (extra) for (const t of s.table) tolls.push(t);
  for (const id of tolls) {
    if (!isTollId(id)) continue;
    const offs = tollOf(id).off;
    for (let k = 0; k < PPS; k++) {
      const from = s.pos[seat * PPS + k];
      if (from < 0) continue;
      const x0 = cx(from);
      const y0 = cy(from);
      for (const [dx, dy] of offs) {
        const [sx, sy] = orient(seat, dx, dy);
        const x = x0 + sx;
        const y = y0 + sy;
        if (x < 0 || x >= W || y < 0 || y >= W) continue;
        const to = y * W + x;
        const there = s.occ[to];
        if (there >= 0 && seatOfPiece(there) === seat) continue;
        out[to]++;
      }
    }
  }
}

const cheb = (a: number, b: number): number => Math.max(Math.abs(cx(a) - cx(b)), Math.abs(cy(a) - cy(b)));

/**
 * Score the position for `me`, in centi-bearers. The Keeper is never scored as material — losing it
 * is MATE — so what the eval actually measures is: who has more bearers, whose Keeper is in danger,
 * whose Keeper is walking, who owns the middle, and (the term that makes this game itself) what the
 * two sides can do with the tolls they hold and the ones sitting on the table.
 */
export function evaluate(s: State, me: number, flat = false, w: EvalWeights = DEFAULT_W): number {
  fillAttacks(s, 0, ATK[0], false);
  fillAttacks(s, 1, ATK[1], false);
  const wantTable = !flat && w.keeperHitTable !== 0;
  if (wantTable) {
    fillAttacks(s, 0, ATK_TABLE[0], true);
    fillAttacks(s, 1, ATK_TABLE[1], true);
  }

  let score = 0;
  for (let seat = 0; seat < 2; seat++) {
    const sign = seat === me ? 1 : -1;
    const foe = 1 - seat;
    let sub = 0;

    let bearers = 0;
    for (let k = 1; k < PPS; k++) {
      const at = s.pos[seat * PPS + k];
      if (at < 0) continue;
      bearers++;
      sub += PSQT[at] * w.activity;
    }
    sub += bearers * w.bearer;

    const kc = s.pos[keeperOf(seat)];
    if (kc >= 0) {
      const target = COFFER[foe];
      const d = cheb(kc, target);
      if (d <= 3) sub += (4 - d) * w.advance;
      if (kc === COFFER[seat] && bearers >= 3) sub += w.home;

      if (!flat) {
        // Being hit is worse when it is about to be their move — but it is never comfortable.
        const urgency = s.turn === foe ? 1 : 0.5;
        const hits = ATK[foe][kc];
        if (hits > 0) sub -= (w.keeperHit + (hits - 1) * w.double) * urgency;
        if (wantTable) {
          const soft = ATK_TABLE[foe][kc] - hits;
          if (soft > 0) sub -= w.keeperHitTable;
        }
      }
    }

    if (!flat) {
      let cover = 0;
      for (let k = 0; k < PPS; k++) {
        const at = s.pos[foe * PPS + k];
        if (at >= 0 && ATK[seat][at] > 0) cover += isKeeper(foe * PPS + k) ? 0 : 1;
      }
      sub += cover * w.cover;
    }

    if (w.hand !== 0) {
      const a = s.hand[seat * 2];
      const b = s.hand[seat * 2 + 1];
      if (isTollId(a)) sub += tollOf(a).w * w.hand;
      if (isTollId(b)) sub += tollOf(b).w * w.hand;
    }
    if (w.table !== 0) {
      // Whatever is lying on the table is picked up by whoever moves next, so it is THEIR asset.
      const next = s.turn;
      let t = 0;
      for (const id of s.table) if (isTollId(id)) t += tollOf(id).w;
      if (seat === next) sub += t * w.table;
    }

    score += sign * sub;
  }

  if (!flat) {
    // Their Keeper standing one toll away from my coffer is the same emergency as my Keeper being
    // attacked, and the eval has to see both or it only ever defends half the game. Measured once
    // from each side rather than inside the loop above, because it depends on the OTHER side's
    // Keeper being the piece that can arrive.
    for (let seat = 0; seat < 2; seat++) {
      const foe = 1 - seat;
      if (keeperCanReach(s, foe, COFFER[seat])) score += (seat === me ? -1 : 1) * w.cofferHit * (s.turn === foe ? 1 : 0.5);
    }
  }
  return Math.round(score);
}

/** Can `seat`'s Keeper land on `cell` next turn, with a toll it holds now? */
function keeperCanReach(s: State, seat: number, cell: number): boolean {
  const from = s.pos[keeperOf(seat)];
  if (from < 0) return false;
  const x0 = cx(from);
  const y0 = cy(from);
  for (let h = 0; h < 2; h++) {
    const id = s.hand[seat * 2 + h];
    if (!isTollId(id)) continue;
    for (const [dx, dy] of tollOf(id).off) {
      const [sx, sy] = orient(seat, dx, dy);
      const x = x0 + sx;
      const y = y0 + sy;
      if (x < 0 || x >= W || y < 0 || y >= W) continue;
      if (y * W + x !== cell) continue;
      const there = s.occ[cell];
      if (there >= 0 && seatOfPiece(there) === seat) continue;
      return true;
    }
  }
  return false;
}

// ── transposition table ─────────────────────────────────────────────────────────
const TT_BITS = 18;
const TT_SIZE = 1 << TT_BITS;
const TT_MASK = TT_SIZE - 1;
const ttVerify = new Int32Array(TT_SIZE);
const ttScore = new Int32Array(TT_SIZE);
const ttMeta = new Int32Array(TT_SIZE);
const ttPly = new Int32Array(TT_SIZE);
let ttGen = 1;

const F_EXACT = 0;
const F_LOWER = 1;
const F_UPPER = 2;

/**
 * Bump the generation and clear the ordering heuristics. The sim calls this before every game — an
 * entry left over from game N would change move ordering in game N+1 and quietly make a
 * "reproducible" simulation not reproducible. A generation stamp does it without a 4MB memset.
 */
export function resetSearch(): void {
  ttGen = (ttGen + 1) & 0xff;
  if (ttGen === 0) {
    ttGen = 1;
    ttVerify.fill(0);
    ttMeta.fill(0);
  }
  killers.fill(-1);
  history.fill(0);
}

const MAX_DIST = 64;
const killers = new Int32Array(MAX_DIST * 2).fill(-1);
const history = new Int32Array(2 * NC * NC);

// ── the search ──────────────────────────────────────────────────────────────────
interface Ctx {
  pool: State[];
  nodes: number;
  deadline: number;
  maxNodes: number;
  aborted: boolean;
  flat: boolean;
  w: EvalWeights;
}

const scratchOrder: number[] = [];

/** A ply that ends the game on the spot. Flagged at ordering time — one comparison, huge payoff. */
function winning(s: State, ply: number): boolean {
  if (plyKind(ply) !== K_MOVE) return false;
  const to = plyTo(ply);
  const victim = s.occ[to];
  if (victim >= 0 && isKeeper(victim)) return true;
  const mover = s.occ[plyFrom(ply)];
  return mover >= 0 && isKeeper(mover) && to === COFFER[1 - s.turn];
}

function orderKey(s: State, ply: number, dist: number, ttBest: number): number {
  if (ply === ttBest) return 3_000_000;
  if (winning(s, ply)) return 2_000_000;
  if (plyKind(ply) !== K_MOVE) return -1000;
  const to = plyTo(ply);
  const victim = s.occ[to];
  if (victim >= 0) return 1_000_000 + 100;
  if (dist < MAX_DIST) {
    if (killers[dist * 2] === ply) return 900;
    if (killers[dist * 2 + 1] === ply) return 850;
  }
  const from = plyFrom(ply);
  const h = Math.min(800, history[s.turn * NC * NC + from * NC + to]);
  return h + PSQT[to] - PSQT[from];
}

function sortPlies(s: State, plies: number[], dist: number, ttBest: number): void {
  scratchOrder.length = 0;
  for (const p of plies) scratchOrder.push(orderKey(s, p, dist, ttBest));
  // Selection sort over a list of ~10–40 — cheaper than a comparator closure, and it lets the
  // caller stop early on a cutoff without having paid to order the tail.
  for (let i = 0; i < plies.length; i++) {
    let best = i;
    for (let j = i + 1; j < plies.length; j++) if (scratchOrder[j] > scratchOrder[best]) best = j;
    if (best !== i) {
      const tp = plies[i];
      plies[i] = plies[best];
      plies[best] = tp;
      const ts = scratchOrder[i];
      scratchOrder[i] = scratchOrder[best];
      scratchOrder[best] = ts;
    }
  }
}

function qsearch(ctx: Ctx, s: State, alpha: number, beta: number, dist: number, qd: number): number {
  ctx.nodes++;
  const stand = evaluate(s, s.turn, ctx.flat, ctx.w);
  if (qd <= 0 || stand >= beta) return stand;
  if (stand > alpha) alpha = stand;

  const plies = genPlies(s);
  const child = ctx.pool[Math.min(dist, ctx.pool.length - 1)];
  let best = stand;
  for (const ply of plies) {
    const isWin = winning(s, ply);
    if (!isWin) {
      if (plyKind(ply) !== K_MOVE) continue;
      if (s.occ[plyTo(ply)] < 0) continue; // quiet — the horizon is captures and wins only
    }
    copyInto(child, s);
    step(child, ply);
    const score = isOver(child)
      ? child.winner === s.turn
        ? MATE - dist
        : -(MATE - dist)
      : -qsearch(ctx, child, -beta, -alpha, dist + 1, qd - 1);
    if (score > best) best = score;
    if (best > alpha) alpha = best;
    if (alpha >= beta) break;
  }
  return best;
}

function negamax(ctx: Ctx, s: State, depth: number, alphaIn: number, beta: number, dist: number): number {
  if ((ctx.nodes & 255) === 0) {
    if (ctx.deadline > 0 && Date.now() > ctx.deadline) ctx.aborted = true;
    if (ctx.maxNodes > 0 && ctx.nodes > ctx.maxNodes) ctx.aborted = true;
  }
  if (ctx.aborted) return 0;
  ctx.nodes++;

  let alpha = alphaIn;
  const hash = positionHash(s);
  const slot = hash & TT_MASK;
  const verify = Math.floor(hash / TT_SIZE) | 0;
  let ttBest = -1;
  if (ttVerify[slot] === verify && (ttMeta[slot] >>> 8) === ttGen) {
    const d = ttMeta[slot] & 63;
    const flag = (ttMeta[slot] >>> 6) & 3;
    ttBest = ttPly[slot];
    if (d >= depth) {
      const v = ttScore[slot];
      if (flag === F_EXACT) return v;
      if (flag === F_LOWER && v > alpha) alpha = v;
      else if (flag === F_UPPER && v < beta) beta = v;
      if (alpha >= beta) return v;
    }
  }

  if (depth <= 0) return qsearch(ctx, s, alpha, beta, dist, 4);

  const plies = genPlies(s);
  if (plies.length === 0) return -(MATE - dist); // unreachable — genPlies always offers a slack ply
  sortPlies(s, plies, dist, ttBest);

  const child = ctx.pool[Math.min(dist, ctx.pool.length - 1)];
  const mover = s.turn;
  let best = -Infinity;
  let bestPly = plies[0];

  for (let i = 0; i < plies.length; i++) {
    const ply = plies[i];
    copyInto(child, s);
    step(child, ply);
    const score = isOver(child)
      ? child.winner === mover
        ? MATE - dist
        : -(MATE - dist)
      : -negamax(ctx, child, depth - 1, -beta, -alpha, dist + 1);
    if (ctx.aborted) return 0;

    if (score > best) {
      best = score;
      bestPly = ply;
    }
    if (best > alpha) alpha = best;
    if (alpha >= beta) {
      if (plyKind(ply) === K_MOVE && s.occ[plyTo(ply)] < 0 && dist < MAX_DIST) {
        if (killers[dist * 2] !== ply) {
          killers[dist * 2 + 1] = killers[dist * 2];
          killers[dist * 2] = ply;
        }
        const idx = mover * NC * NC + plyFrom(ply) * NC + plyTo(ply);
        history[idx] = Math.min(1 << 20, history[idx] + depth * depth);
      }
      break;
    }
  }

  ttVerify[slot] = verify;
  ttScore[slot] = best;
  ttPly[slot] = bestPly;
  ttMeta[slot] = (Math.min(63, depth) | ((best <= alphaIn ? F_UPPER : best >= beta ? F_LOWER : F_EXACT) << 6) | (ttGen << 8)) | 0;
  return best;
}

/** Iterative deepening from the root. Returns the best ply found within the budget. */
export function searchTurn(real: State, opts: SearchOpts): SearchResult {
  const started = Date.now();
  const modeDepth = real.mode.table > 1 ? 1 : 0; // Arrears roughly doubles the branching factor
  const maxDepth = Math.max(1, opts.depth - modeDepth);
  // Search from a DETACHED root. The flags live on the state and `copyInto` propagates them from
  // the parent, so searching directly off the real game state silently reset `frozenTolls` to false
  // on the very first copy — which made the sim's control arm byte-identical to the sighted one.
  // A control that produces identical numbers is not a strict control, it is a broken one.
  const s = cloneState(real);
  s.searchMode = true;
  s.frozenTolls = !!opts.frozenTolls;
  const ctx: Ctx = {
    pool: Array.from({ length: maxDepth + 8 }, () => {
      const c = cloneState(s);
      c.searchMode = true;
      c.frozenTolls = !!opts.frozenTolls;
      return c;
    }),
    nodes: 0,
    deadline: opts.timeMs && opts.timeMs > 0 ? started + opts.timeMs : 0,
    maxNodes: opts.nodes ?? 0,
    aborted: false,
    flat: !!opts.flat,
    w: opts.weights ?? DEFAULT_W,
  };

  const root = genPlies(s);
  if (root.length === 0) return { ply: -1, score: -MATE, depth: 0, nodes: 0, ms: 0 };
  // Never blunder a win, at any tier, even at depth 0 with the clock already gone.
  for (const ply of root) if (winning(s, ply)) return { ply, score: MATE, depth: 0, nodes: 0, ms: Date.now() - started };
  if (root.length === 1) return { ply: root[0], score: 0, depth: 0, nodes: 0, ms: Date.now() - started };

  sortPlies(s, root, 0, -1);
  const noise = opts.noise ?? 0;
  const rng = opts.rng ?? makeRng((s.seed ^ (s.ply * 2654435761)) >>> 0);

  let bestPly = root[0];
  let bestScore = -Infinity;
  let reached = 0;

  for (let depth = 1; depth <= maxDepth; depth++) {
    let localBest = -Infinity;
    let localPly = bestPly;
    let alpha = -Infinity;
    const child = ctx.pool[0];
    const ordered = [bestPly, ...root.filter((p) => p !== bestPly)];

    for (const ply of ordered) {
      copyInto(child, s);
      step(child, ply);
      let score = isOver(child)
        ? child.winner === s.turn
          ? MATE
          : -MATE
        : -negamax(ctx, child, depth - 1, -Infinity, -alpha, 1);
      if (ctx.aborted) break;
      if (noise > 0) score += (rng() - 0.5) * 2 * noise;
      if (score > localBest) {
        localBest = score;
        localPly = ply;
      }
      if (score > alpha) alpha = score;
    }
    if (ctx.aborted) break;
    bestPly = localPly;
    bestScore = localBest;
    reached = depth;
    if (bestScore >= MATE - depth) break;
  }

  return { ply: bestPly, score: bestScore, depth: reached, nodes: ctx.nodes, ms: Date.now() - started };
}

// ── the opening ─────────────────────────────────────────────────────────────────
/**
 * The split. The splitter's problem is exactly a pie cut: the chooser will take whichever pair it
 * likes better, so the splitter must maximise the value of the pair it is LEFT with. That is
 * computed here honestly — for each partition, play out the chooser's best reply and score the
 * position the splitter is then stuck with — rather than guessed from a preference table, because
 * the whole fairness claim in tests/balance.test.ts rests on the splitter pricing its own first
 * move correctly.
 */
/**
 * How deep the opening probe searches. A STATIC evaluation is useless here: at the split the two
 * sides have identical material and identical geometry, so the only thing a static score can see is
 * the toll-value term — a proxy so weak that the first version of this function produced a 60/40
 * opening and a counter-control WITHOUT the pie rule that was fairer than the one with it. A short
 * real search actually plays the position out, which is the only honest way to price a pair.
 */
const OPENING_DEPTH = 3;

function openingValue(s: State, forSeat: number, opts: SearchOpts): number {
  const r = searchTurn(s, {
    depth: OPENING_DEPTH,
    timeMs: 0,
    nodes: 6000,
    flat: !!opts.flat,
    noise: 0,
    weights: opts.weights,
    frozenTolls: opts.frozenTolls,
  });
  // searchTurn scores from the side to move; flip it if that is not the seat we are asking about.
  return s.turn === forSeat ? r.score : -r.score;
}

export function chooseSplit(s: State, opts: SearchOpts): number {
  const legal = genPlies(s);
  if (legal.length === 0) return -1;
  const rng = opts.rng ?? makeRng((s.seed ^ 0x5f3a) >>> 0);
  const probe = cloneState(s);
  probe.searchMode = true;
  const probe2 = cloneState(s);
  probe2.searchMode = true;
  probe.frozenTolls = probe2.frozenTolls = false; // the split itself never rotates a toll

  let best = legal[0];
  let bestScore = -Infinity;
  for (const ply of legal) {
    copyInto(probe, s);
    step(probe, ply);
    // The chooser takes whichever pair is better FOR THE CHOOSER, so the splitter is left with the
    // other one and must maximise the minimum. That is the whole pie cut, in six lines.
    let leftWith = Infinity;
    for (const c of genPlies(probe)) {
      copyInto(probe2, probe);
      step(probe2, c);
      const forSplitter = openingValue(probe2, 0, opts);
      if (forSplitter < leftWith) leftWith = forSplitter;
    }
    const score = leftWith + (opts.noise ? (rng() - 0.5) * opts.noise * 2 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = ply;
    }
  }
  return best;
}

/** The chooser's side of the pie cut: take the pair that leaves you better off. */
export function chooseTake(s: State, opts: SearchOpts): number {
  const legal = genPlies(s);
  if (legal.length === 0) return -1;
  const rng = opts.rng ?? makeRng((s.seed ^ 0x77a1) >>> 0);
  const probe = cloneState(s);
  probe.searchMode = true;
  let best = legal[0];
  let bestScore = -Infinity;
  for (const ply of legal) {
    copyInto(probe, s);
    step(probe, ply);
    const score = openingValue(probe, 1, opts) + (opts.noise ? (rng() - 0.5) * opts.noise * 2 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = ply;
    }
  }
  return best;
}

/** The one entry point the game screen uses: pick a ply for whatever phase we are in. */
export function chooseAny(s: State, opts: SearchOpts): number {
  if (s.phase === PH_SPLIT) return chooseSplit(s, opts);
  if (s.phase === PH_CHOOSE) return chooseTake(s, opts);
  if (s.phase === PH_PLAY) return searchTurn(s, opts).ply;
  return -1;
}

export function optsForTier(t: Tier, seed = 0): SearchOpts {
  return {
    depth: t.depth,
    timeMs: t.timeMs,
    flat: t.flat,
    noise: t.noise,
    rng: makeRng((seed ^ 0x9e37) >>> 0),
  };
}

/**
 * A one-ply greedy control for the balance sim: take a win, then take material, then walk the
 * Keeper. Deliberately NOT the Novice tier — a control the sim compares against must be a different
 * animal, or "the bot beats the control" is just the bot beating itself.
 */
export function greedyPly(s: State, rng: Rng): number {
  const plies = genPlies(s);
  if (plies.length === 0) return -1;
  let best = plies[0];
  let bestScore = -Infinity;
  for (const ply of plies) {
    let score: number;
    if (plyKind(ply) !== K_MOVE) score = -100 + rng();
    else if (winning(s, ply)) score = 1e6;
    else {
      const victim = s.occ[plyTo(ply)];
      score = (victim >= 0 ? 500 : 0);
      const mover = s.occ[plyFrom(ply)];
      if (mover >= 0 && isKeeper(mover)) score += (4 - Math.max(Math.abs(cx(plyTo(ply)) - cx(COFFER[1 - s.turn])), Math.abs(cy(plyTo(ply)) - cy(COFFER[1 - s.turn])))) * 10;
      score += rng();
    }
    if (score > bestScore) {
      bestScore = score;
      best = ply;
    }
  }
  return best;
}

export function randomPly(s: State, rng: Rng): number {
  const plies = genPlies(s);
  if (plies.length === 0) return -1;
  return plies[Math.floor(rng() * plies.length)];
}
